/**
 * alertTiers.js
 *
 * Defines the three alert tiers and their defaults.
 *
 * Tier 1 — Standard: single signal breach, fast alerts, moderate confirmation
 * Tier 2 — Confirmed: requires RSI + BB confluence, more bars, longer cooldown
 * Tier 3 — High Conviction: RSI + BB + VWAP all agree, strictest thresholds
 *
 * Each tier has fully independent settings, ringtone, and push config.
 * Tiers can each be set to auto-enable when the market opens.
 */

export const TIER_IDS = ['tier1', 'tier2', 'tier3'];

export const TIER_META = {
  tier1: {
    id:          'tier1',
    name:        'Standard',
    description: 'Single signal breach — fast, frequent alerts',
    color:       'var(--accent)',
    badge:       '1',
  },
  tier2: {
    id:          'tier2',
    name:        'Confirmed',
    description: 'RSI + Bollinger confluence required',
    color:       'var(--warn)',
    badge:       '2',
  },
  tier3: {
    id:          'tier3',
    name:        'High Conviction',
    description: 'RSI + BB + VWAP all agree — rare, high-quality setups',
    color:       'var(--down)',
    badge:       '3',
  },
};

export const TIER_DEFAULTS = {
  tier1: {
    enabled:            true,
    autoEnableOnOpen:   false,
    // Signal conditions
    rsiOversold:        30,
    rsiOverbought:      70,
    bbSigma:            1,
    persistBars:        3,
    cooldownMins:       10,
    // Confluence: how many signals must agree (1 = any single signal fires)
    minConfluence:      1,
    vwapDeviationPct:   0,     // 0 = VWAP not required
    requireBBbreach:    false,
    requireVWAPdev:     false,
    enabledSignals:     ['oversold', 'overbought', 'bbLower', 'bbUpper'],
    // Audio
    audioEnabled:       true,
    audioVolume:        0.7,
    ringtoneCutoffSecs: 0,
    gradualVolume:      false,
    // Browser notification
    browserNotif:       false,
    // Pushover (inherits global creds by default, can override per tier)
    pushoverEnabled:    true,
    pushoverPriority:   1,
    pushoverTokenOverride:  '',   // blank = use global token
    pushoverUserOverride:   '',   // blank = use global user key
  },
  tier2: {
    enabled:            true,
    autoEnableOnOpen:   false,
    rsiOversold:        28,
    rsiOverbought:      72,
    bbSigma:            1,
    persistBars:        4,
    cooldownMins:       15,
    minConfluence:      2,        // need 2 signals
    vwapDeviationPct:   0.2,      // price must be 0.2% from VWAP
    requireBBbreach:    true,
    requireVWAPdev:     false,
    enabledSignals:     ['oversold', 'overbought', 'bbLower', 'bbUpper'],
    audioEnabled:       true,
    audioVolume:        0.85,
    ringtoneCutoffSecs: 8,
    gradualVolume:      false,
    browserNotif:       false,
    pushoverEnabled:    true,
    pushoverPriority:   1,
    pushoverTokenOverride:  '',
    pushoverUserOverride:   '',
  },
  tier3: {
    enabled:            true,
    autoEnableOnOpen:   false,
    rsiOversold:        25,
    rsiOverbought:      75,
    bbSigma:            1.5,
    persistBars:        5,
    cooldownMins:       20,
    minConfluence:      3,        // need 3 signals
    vwapDeviationPct:   0.4,      // price must be 0.4% from VWAP
    requireBBbreach:    true,
    requireVWAPdev:     true,
    enabledSignals:     ['oversold', 'overbought', 'bbLower', 'bbUpper'],
    audioEnabled:       true,
    audioVolume:        1.0,
    ringtoneCutoffSecs: 12,
    gradualVolume:      true,
    browserNotif:       true,
    pushoverEnabled:    true,
    pushoverPriority:   2,        // emergency — repeats until acknowledged
    pushoverTokenOverride:  '',
    pushoverUserOverride:   '',
  },
};

// KV key helpers
export const tierSettingsKey = (tierId, username) => `alerts:tier:${tierId}:settings:${username}`;
export const tierRingtoneKey = (tierId, username) => `alerts:tier:${tierId}:ringtone:${username}`;
